import type { BluetoothPrinterService } from './BluetoothPrinterService';
import { encodeXprinterWakeBytes } from './escpos/xprinterVendorCommands';
import { BLE_KEEP_ALIVE_INTERVAL_SEC, getPrinterPowerSettings } from './printerPowerSettings';
import { printerDebugWarn } from './printerDebug';

type KeepAliveHost = Pick<
  BluetoothPrinterService,
  'getActiveMode' | 'getConnectionState' | 'isPrintWriteInFlight' | 'writeControlBytes'
>;

let intervalId: ReturnType<typeof globalThis.setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;
let hostRef: KeepAliveHost | null = null;
let lastErrorLoggedAt = 0;

function isTabVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

async function tickKeepAlive(): Promise<void> {
  const host = hostRef;
  if (!host) return;
  if (!isTabVisible()) return;
  if (host.getActiveMode() !== 'ble') return;
  if (!getPrinterPowerSettings().bleKeepAliveEnabled) return;

  const state = host.getConnectionState();
  if (state !== 'connected') return;
  if (host.isPrintWriteInFlight()) return;

  try {
    await host.writeControlBytes(encodeXprinterWakeBytes());
  } catch (e) {
    const now = Date.now();
    if (now - lastErrorLoggedAt > 60_000) {
      lastErrorLoggedAt = now;
      printerDebugWarn('BLE keep-alive write failed', e);
    }
  }
}

export function startBleKeepAlive(host: KeepAliveHost): void {
  stopBleKeepAlive();
  if (!getPrinterPowerSettings().bleKeepAliveEnabled) return;

  hostRef = host;
  const intervalMs = BLE_KEEP_ALIVE_INTERVAL_SEC * 1000;
  intervalId = globalThis.setInterval(() => {
    void tickKeepAlive();
  }, intervalMs);

  if (typeof document !== 'undefined') {
    visibilityHandler = () => {
      if (isTabVisible()) {
        void tickKeepAlive();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }
}

export function stopBleKeepAlive(): void {
  if (intervalId !== null) {
    globalThis.clearInterval(intervalId);
    intervalId = null;
  }
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  hostRef = null;
  lastErrorLoggedAt = 0;
}

export function restartBleKeepAliveIfRunning(host: KeepAliveHost): void {
  if (intervalId === null) return;
  startBleKeepAlive(host);
}
